import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuthTransitionStore } from '@/stores/useAuthTransitionStore';

const ICON_CENTER_SIZE = 112;
const MIN_CENTER_HOLD_MS = 1400;

export function AuthTransitionOverlay() {
    const { phase, loginIconRect, sidebarIconRect, moveToCenter, setSidebarRect, moveToSidebar, finish, reset } =
        useAuthTransitionStore();

    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const centerReadyRef = useRef(false);
    const sidebarFoundRef = useRef(false);
    const [shimmerVisible, setShimmerVisible] = useState(false);

    // to-center → at-center
    useEffect(() => {
        if (phase !== 'to-center') return;
        const timer = setTimeout(() => {
            moveToCenter();
            setShimmerVisible(true);
        }, 600);
        return () => clearTimeout(timer);
    }, [phase, moveToCenter]);

    // at-center → sidebar bul + minimum bekleme
    useEffect(() => {
        if (phase !== 'at-center') return;
        centerReadyRef.current = false;
        sidebarFoundRef.current = false;

        const holdTimer = setTimeout(() => {
            centerReadyRef.current = true;
            if (sidebarFoundRef.current) {
                setShimmerVisible(false);
                setTimeout(() => moveToSidebar(), 100);
            }
        }, MIN_CENTER_HOLD_MS);

        const poll = () => {
            const el = document.querySelector('[data-sidebar-logo]') as HTMLElement | null;
            if (el) {
                const rect = el.getBoundingClientRect();
                setSidebarRect({ x: rect.left, y: rect.top, width: rect.width, height: rect.height });
                sidebarFoundRef.current = true;
                if (pollRef.current) clearInterval(pollRef.current);
                if (centerReadyRef.current) {
                    setShimmerVisible(false);
                    setTimeout(() => moveToSidebar(), 100);
                }
            }
        };
        poll();
        pollRef.current = setInterval(poll, 80);

        const safetyTimer = setTimeout(() => {
            if (pollRef.current) clearInterval(pollRef.current);
            setShimmerVisible(false);
            finish();
            setTimeout(reset, 400);
        }, 5000);

        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
            clearTimeout(holdTimer);
            clearTimeout(safetyTimer);
        };
    }, [phase, setSidebarRect, moveToSidebar, finish, reset]);

    // to-sidebar → reset
    useEffect(() => {
        if (phase !== 'to-sidebar') return;
        const timer = setTimeout(() => {
            reset();
        }, 520);
        return () => clearTimeout(timer);
    }, [reset]);

    if (phase === 'idle') return null;

    const centerX = typeof window !== 'undefined' ? window.innerWidth / 2 : 500;
    const centerY = typeof window !== 'undefined' ? window.innerHeight / 2 : 400;

    const getTarget = () => {
        switch (phase) {
            case 'to-center':
            case 'at-center':
                return {
                    x: centerX - ICON_CENTER_SIZE / 2,
                    y: centerY - ICON_CENTER_SIZE / 2,
                    size: ICON_CENTER_SIZE,
                };
            case 'to-sidebar':
            case 'done':
                return sidebarIconRect
                    ? { x: sidebarIconRect.x, y: sidebarIconRect.y, size: sidebarIconRect.width }
                    : { x: centerX - ICON_CENTER_SIZE / 2, y: centerY - ICON_CENTER_SIZE / 2, size: ICON_CENTER_SIZE };
            default:
                return { x: 0, y: 0, size: 36 };
        }
    };

    const target = getTarget();
    const isFullOverlay = phase === 'to-center' || phase === 'at-center';
    const startSize = loginIconRect?.width ?? 36;

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 10000, pointerEvents: (phase === 'to-center' || phase === 'at-center') ? 'all' : 'none' }}>
            {/* Solid backdrop */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: isFullOverlay ? 1 : 0 }}
                transition={{ duration: isFullOverlay ? 0.25 : 0.45 }}
                style={{ position: 'absolute', inset: 0, backgroundColor: '#fafafa' }}
                className="dark:!bg-[#0a0a0a]"
            />

            {/* Icon — no background, no border-radius box, just the SVG */}
            <motion.img
                src="/oa-logo-svg1.svg"
                alt=""
                aria-hidden
                initial={
                    loginIconRect
                        ? { x: loginIconRect.x, y: loginIconRect.y, width: startSize, height: startSize, opacity: 1 }
                        : { x: centerX - 18, y: centerY - 18, width: 36, height: 36, opacity: 1 }
                }
                animate={{
                    x: target.x,
                    y: target.y,
                    width: target.size,
                    height: target.size,
                    opacity: 1,
                }}
                transition={
                    phase === 'to-center'
                        ? { type: 'spring', stiffness: 80, damping: 18, mass: 0.8 }
                        : phase === 'to-sidebar'
                          ? { type: 'spring', stiffness: 100, damping: 20, mass: 0.7 }
                          : { duration: 0.25 }
                }
                style={{ position: 'absolute', objectFit: 'contain', willChange: 'transform' }}
            />

            {/* Shimmer sweep — positioned over the icon */}
            <AnimatePresence>
                {shimmerVisible && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        style={{
                            position: 'absolute',
                            left: centerX - ICON_CENTER_SIZE / 2,
                            top: centerY - ICON_CENTER_SIZE / 2,
                            width: ICON_CENTER_SIZE,
                            height: ICON_CENTER_SIZE,
                            overflow: 'hidden',
                            borderRadius: '1rem',
                            pointerEvents: 'none',
                        }}
                    >
                        <motion.div
                            animate={{ x: ['-100%', '250%'] }}
                            transition={{ duration: 1.3, ease: 'easeInOut', repeat: Infinity, repeatDelay: 0.4 }}
                            style={{
                                position: 'absolute',
                                inset: 0,
                                background: 'linear-gradient(105deg, transparent 0%, rgba(255,255,255,0.45) 45%, rgba(255,255,255,0.7) 50%, rgba(255,255,255,0.45) 55%, transparent 100%)',
                            }}
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
